import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { authFetch } from '../services/authFetch';

const SettingsPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Kata sandi baru dan konfirmasi tidak cocok');
      return;
    }
    if (formData.newPassword.length < 8) {
      setError('Kata sandi baru harus minimal 8 karakter');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Get token from localStorage
      const userData = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (!userData || !token) {
        setError('User tidak ditemukan, silakan login ulang.');
        setLoading(false);
        return;
      }

      // Assume API endpoint
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      // Determine endpoint based on user role
      const userRole = localStorage.getItem('userRole') || (JSON.parse(localStorage.getItem('user'))?.role ?? '');
      let endpoint = '/api/change-password';
      if (userRole === 'student') endpoint = '/api/student/change-password';
      else if (userRole === 'teacher') endpoint = '/api/teacher/change-password';
      const response = await authFetch(`${apiBase}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });
      
      const responseText = await response.clone().text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        setError('Gagal mengurai respons server.');
        setLoading(false);
        return;
      }
      if (!response.ok) {
        setError(data.message || 'Gagal mengubah kata sandi.');
      } else {
        setSuccess(data.message || 'Kata sandi berhasil diubah!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (err) {
      setError('Terjadi kesalahan jaringan.');
    }
    setLoading(false);
  };

  
  return (
    <PageContainer>
      <PageHeader>
        <h1>Pengaturan Akun</h1>
        <p>Kelola pengaturan dan preferensi akun Anda</p>
      </PageHeader>
      
      <SettingsContainer>
        <SettingsSection>
          <SectionTitle>Ubah Kata Sandi</SectionTitle>
          
          <FormContainer>
            {error && <ErrorMessage theme={darkMode ? 'dark' : 'light'}>{error}</ErrorMessage>}
            {success && <SuccessMessage theme={darkMode ? 'dark' : 'light'}>{success}</SuccessMessage>}
            
            <form onSubmit={handleSubmit}>
              <FormGroup theme={darkMode ? 'dark' : 'light'}>
                <label htmlFor="currentPassword">Kata Sandi Saat Ini</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup theme={darkMode ? 'dark' : 'light'}>
                <label htmlFor="newPassword">Kata Sandi Baru</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                />
                <FormHint>Minimal 8 karakter</FormHint>
              </FormGroup>
              
              <FormGroup theme={darkMode ? 'dark' : 'light'}>
                <label htmlFor="confirmPassword">Konfirmasi Kata Sandi Baru</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <ButtonContainer>
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Menyimpan...' : 'Ubah Kata Sandi'}
                </SubmitButton>
              </ButtonContainer>
            </form>
          </FormContainer>
        </SettingsSection>
        
        <SecurityInfoBox>
          <SecurityIcon>🔒</SecurityIcon>
          <SecurityContent>
            <SecurityTitle>Keamanan Akun</SecurityTitle>
            <SecurityText>
              Pastikan untuk menggunakan kata sandi yang kuat dan tidak 
              menggunakan kata sandi yang sama dengan akun lain. Kata sandi 
              yang kuat terdiri dari kombinasi huruf, angka, dan karakter khusus.
            </SecurityText>
          </SecurityContent>
        </SecurityInfoBox>
      </SettingsContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const SettingsContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsSection = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border);
`;

const FormContainer = styled.div`
  max-width: 500px;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#2a2a2a' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    }
  }
`;

const FormHint = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  margin-top: 0.5rem;
`;

const ButtonContainer = styled.div`
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
  }
`;

const SecurityInfoBox = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: background-color 0.3s, box-shadow 0.3s;
`;

const SecurityIcon = styled.div`
  font-size: 2rem;
  line-height: 1;
`;

const SecurityContent = styled.div`
  flex: 1;
`;

const SecurityTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
`;

const SecurityText = styled.p`
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-light);
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme === 'dark' ? '#3a1c1c' : '#feeeee'};
  color: ${props => props.theme === 'dark' ? '#ff6b6b' : '#dc3545'};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  border-left: 3px solid ${props => props.theme === 'dark' ? '#ff6b6b' : '#dc3545'};
  display: flex;
  align-items: center;
  
  &:before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme === 'dark' ? '#1a3a2a' : '#e6f8f5'};
  color: ${props => props.theme === 'dark' ? '#4ade80' : '#00a389'};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  border-left: 3px solid ${props => props.theme === 'dark' ? '#4ade80' : '#00a389'};
  display: flex;
  align-items: center;
  
  &:before {
    content: "✅";
    margin-right: 0.5rem;
  }
`;

export default SettingsPage;